import React, { useContext, useState } from "react";
import CenterModal from "@/designs/CenterModal";
import Input from "@/designs/Input";
import { Lock } from "lucide-react";
import { useResetPaswordMutation } from "@/graphql/generated/schema";
import { CMSModal } from "@/context";
import { client } from "@/client/apolloClient";
import Loader from "@/designs/Loader";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import Button from "@/designs/Button";

import { useTranslation } from "react-i18next";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { user } = useContext(CMSModal);
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [changePassword, { loading }] = useResetPaswordMutation();
  const router = useRouter();

  const handleChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error(t("changePassword.toasts.mismatch"));
      return;
    }

    try {
      const data = await changePassword({
        variables: {
          email: user?.email,
          password: formData.newPassword,
        },
      });
      if (data.data?.resetPasword?.user) {
        toast.success(t("changePassword.toasts.success"));
        onClose();
        localStorage.removeItem("token");
        await client.clearStore();
        router.push("/login");
      } else {
        toast.error(data.data?.resetPasword?.error?.message || t("changePassword.toasts.error"));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("changePassword.toasts.error"));
    }
  };

  return (
    <CenterModal isOpen={isOpen} onClose={onClose} title={t("changePassword.title")}>
      {loading && <Loader />}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t("changePassword.newPassword")}
          type="password"
          name="newPassword"
          value={formData.newPassword}
          onChange={(value) => handleChange("newPassword", value)}
          required
          icon={<Lock size={16} />}
        />
        <Input
          label={t("changePassword.confirmPassword")}
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={(value) => handleChange("confirmPassword", value)}
          required
          icon={<Lock size={16} />}
        />

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" onClick={onClose} variant="outline">
            {t("changePassword.cancel")}
          </Button>
          <Button type="submit">{t("changePassword.update")}</Button>
        </div>
      </form>
    </CenterModal>
  );
};

export default ChangePasswordModal;
